/**
 * Simple form module that can be reused lots
 * 
 * Some useful bits of info that aren't immediately obvious:
 * - Your submit button needs a `.js-submit` class
 * - 'preCheck' must return true to actually continue form submission, use 'before' to execute some code each
 * time the form is submitted. 'addToForm' sort of does that, but that also does something with the return value.
 * 
 */

import AjaxFormErrorHandler from "Scripts/common/ajax-form-error-handler";
import fetchJson from 'Scripts/common/fetch-json';
import LoadingButton from 'Scripts/common/loading-button';
import showErrorBar from 'Scripts/common/show-error-bar';

// Function used when options which require functions aren't present.
const NO_OP_FUNCTION = function() { 
	/* does nothing */
}

/**
 * @typedef {object} FormOptions
 * @property {?function(FormData): void} [addToForm=null] - A function that runs just before submission, to add additional data to the request (e.g. cropper images or additional data in a modal).
 * @property {?function(): void} [always=null] - A function that runs afterwards, when the submission is successful or not.
 * @property {LoadingButton|boolean} [button=false] - The submit button for the form, wrapped in a LoadingButton instance, or `false` if no button is provided.
 * @property {?function(object): void} [callback=null] - A function that runs after a successful form submission, receiving the response data.
 * @property {string} [endpoint] - Endpoint for the form submission. Will usually be taken from the form element's `action` attribute but can be manually passed too.
 * @property {AjaxFormErrorHandler} errorHandler - Instance for handling form submission errors.
 * @property {HTMLFormElement} form - The HTML `<form>` element.
 * @property {?function(): boolean} [preCheck=null] - A function to run before the submission starts, e.g., Recaptcha. Should return `true` to proceed with submission, `false` otherwise.
 * @property {boolean} [willRedirect=false] - Indicates if the page will redirect if successful. This is used to enable the success state of the submit button.
 * @property {?function(Error): void} [onError] - A function that's called when an error is thrown in either the response -> JSON conversion, or any 'then' blocks. Defaults to `showErrorBar`.
 * @property {function(): void} [before] - A function that always executes before form submission. Useful for cleaning up form errors and other bits. Defaults to a no-op function.
 */

export default class Form {
    /**
     * @param {FormOptions} options 
     */
	constructor(options) {
		if(options) {
			this.setup(options);
		}
	}

	// Set up the object
	setup(options) {
		options    = options || this.options || {};
		let form   = $(options.form);
		let button = options.button || form.find(".js-submit");

		if(!form || !form.length) {
			console.warn("Form.setup()", "No form found")
			return false;
		}

		this.options = {
			addToForm:    options.addToForm || null,
			always:       options.always || null,
			button:       button ? new LoadingButton(button) : false,
			callback:     options.callback || null,
			endpoint:     options.endpoint || form.attr('action'),
			errorHandler: new AjaxFormErrorHandler({parent:form}),
			form:         form,
			preCheck:     options.preCheck || null,
			willRedirect: options.willRedirect || false,
			onError:      options.onError || showErrorBar,
			before: 	  options.before || NO_OP_FUNCTION
		};

		console.log("Form.setup()", options, this.options);

		form.on('submit', this.submit.bind(this));
	}

	async submit(e) {
		e.preventDefault();

		if(this.options.preCheck && !this.options.preCheck()) {
			console.log(`Form.submit', 'Halting form submission because #preCheck didn't return true.`);
			return;
		}
		
		this.changeButton(false);

		let formData = new FormData(e.currentTarget);

		if(this.options.addToForm) {
			formData = this.options.addToForm(formData);
		}

		console.log('Form data:');
		for(let pair of formData.entries()) {
			console.log(`name=${pair[0]}, value=${pair[1]}`);
		}

		this.options.before();

		const { success, result } = await fetchJson(this.options.endpoint, { 
			method: 'POST',
			body: formData,
		});

		// Handle non-form errors, e.g. server errors
		if(!success) {
			console.error(result.error);
			this.options.onError(result.error);
			this.enableButton();
			return;
		}

		console.log("Form.submit.result", result);
		
		// Get any errors
		let validation = result.payload || result;
		validation = validation.validation || result;

		let globalError = $("#oops");

		// Handle errors
		if(this.options.errorHandler.handleErrors(validation)) {
			globalError.show();
		} else {
			globalError.hide();

			// If reloading the page, this ensure we go to the top
			history.scrollRestoration = 'manual';

			if(this.options.callback) {
				this.options.callback(result);
			}
		}

		// Custom functions that run on success or if errors
		if(this.options.always) {
			this.options.always(result);
		}

		// Update the button
		if(this.options.willRedirect && validation.success) {
			this.successButton();
		} else {
			this.enableButton();
		}
	}

	enableButton() {
		this.changeButton(true);
	}

	changeButton(enabled) {
		let button = this.options.button;
		if(button) {
			enabled ? button.enable() : button.disable();
		}
	}

	successButton() {
		let button = this.options.button;
		if(button) {
			button.success();
		}
	}
}
